<template>
  <div class="mx-auto w-full max-w-6xl px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
    <nav class="mb-6" aria-label="Tagasi">
      <button
        type="button"
        class="inline-flex items-center gap-1.5 rounded-md text-sm font-medium text-slate-600 transition-colors hover:text-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
        @click="goBack"
      >
        <svg
          class="h-4 w-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
          />
        </svg>
        Tagasi otsingusse
      </button>
    </nav>

    <!-- Loading -->
    <div
      v-if="loading"
      class="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
      aria-busy="true"
    >
      <p class="sr-only" role="status">Laen andmeid…</p>
      <div
        class="grid animate-pulse lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]"
        aria-hidden="true"
      >
        <div class="p-6 sm:p-8">
          <div class="mx-auto aspect-square w-full max-w-md rounded-xl bg-slate-100"></div>
        </div>
        <div class="space-y-4 p-6 sm:p-8">
          <div class="h-3 w-24 rounded bg-slate-100"></div>
          <div class="h-7 w-5/6 rounded bg-slate-100"></div>
          <div class="h-7 w-2/3 rounded bg-slate-100"></div>
          <div class="h-4 w-1/2 rounded bg-slate-100"></div>
          <div class="h-28 w-full rounded-xl bg-slate-100"></div>
        </div>
      </div>
    </div>

    <!-- Error -->
    <div
      v-else-if="error"
      role="alert"
      class="rounded-2xl border border-red-200 bg-white p-10 text-center shadow-sm"
    >
      <p class="font-semibold text-red-700">{{ error }}</p>
      <button type="button" :class="[primaryButton, 'mt-6']" @click="goBack">
        Tagasi
      </button>
    </div>

    <!-- Product -->
    <template v-else-if="article">
      <article
        class="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm"
      >
        <div class="grid lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
          <div
            class="border-b border-slate-200 p-6 sm:p-8 lg:border-b-0 lg:border-r"
          >
            <div
              class="relative mx-auto aspect-square w-full max-w-md"
              aria-live="polite"
            >
              <div
                v-if="!imageUrl || imageFailed || !imageLoaded"
                class="absolute inset-0 flex flex-col items-center justify-center gap-3 rounded-xl bg-slate-50 text-slate-400"
              >
                <svg
                  class="h-16 w-16 text-slate-300"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.25"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" />
                  <circle cx="12" cy="12" r="4.5" />
                  <circle cx="12" cy="12" r="1.25" />
                </svg>
                <p
                  class="text-sm font-medium"
                  :class="imageFailed ? 'text-red-600' : 'text-slate-500'"
                >
                  {{ imageStatusText }}
                </p>
              </div>
              <img
                v-if="imageUrl && !imageFailed"
                :key="imageUrl"
                :src="imageUrl"
                :alt="`Pilt artiklist ${article.ArticleText}`"
                width="448"
                height="448"
                decoding="async"
                class="relative h-full w-full object-contain"
                @load="imageLoaded = true"
                @error="imageFailed = true"
              />
            </div>
          </div>

          <div class="flex flex-col p-6 sm:p-8">
            <p
              class="text-xs font-semibold uppercase tracking-[0.16em] text-brand-600"
            >
              {{ article.BrandName || "Tundmatu tootja" }}
            </p>
            <h1
              class="mt-2 text-2xl font-extrabold leading-tight tracking-tight text-ink sm:text-3xl"
            >
              {{ article.ArticleText }}
            </h1>
            <p
              class="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500"
            >
              <span
                >Artikli ID:
                <strong class="font-semibold text-slate-700">{{
                  article.ArticleId
                }}</strong></span
              >
              <span v-if="article.PatternModelText"
                >Mudel:
                <strong class="font-semibold text-slate-700">{{
                  article.PatternModelText
                }}</strong></span
              >
            </p>

            <ul v-if="specTags.length" class="mt-5 flex flex-wrap gap-2">
              <li
                v-for="tag in specTags"
                :key="tag"
                class="rounded-md bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 ring-1 ring-inset ring-slate-200"
              >
                {{ tag }}
              </li>
            </ul>

            <div class="mt-6 rounded-xl bg-slate-50 p-5 ring-1 ring-inset ring-slate-200">
              <p class="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Netohind
              </p>
              <p class="mt-1 text-3xl font-extrabold tracking-tight text-brand-600 tabular-nums sm:text-4xl">
                {{ netPriceFormatted }}
              </p>
              <p class="mt-1.5 text-sm text-slate-600">
                Hind koos KM (24%):
                <span class="font-semibold text-ink tabular-nums">{{
                  vatPriceFormatted
                }}</span>
              </p>
            </div>

            <dl class="mt-4 grid gap-3 sm:grid-cols-2">
              <div
                class="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3"
              >
                <span
                  class="h-2.5 w-2.5 shrink-0 rounded-full"
                  :class="localStock > 0 ? 'bg-emerald-500' : 'bg-slate-300'"
                  aria-hidden="true"
                ></span>
                <div>
                  <dt class="text-xs text-slate-500">Kohalik laoseis</dt>
                  <dd class="text-sm font-semibold text-ink tabular-nums">
                    {{ localStock }} tk
                  </dd>
                </div>
              </div>
              <div
                class="flex items-center gap-3 rounded-xl border border-slate-200 px-4 py-3"
              >
                <span
                  class="h-2.5 w-2.5 shrink-0 rounded-full"
                  :class="externalStock > 0 ? 'bg-brand' : 'bg-slate-300'"
                  aria-hidden="true"
                ></span>
                <div>
                  <dt class="text-xs text-slate-500">Väline laoseis</dt>
                  <dd class="text-sm font-semibold text-ink tabular-nums">
                    {{ externalStock }} tk
                  </dd>
                </div>
              </div>
            </dl>

            <div class="mt-6 flex flex-wrap gap-3 lg:mt-auto lg:pt-6">
              <a :href="inquiryMailto" :class="primaryButton">
                <svg
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="2"
                  stroke="currentColor"
                  aria-hidden="true"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                  />
                </svg>
                Küsi lisainfot
              </a>
              <button
                type="button"
                class="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-ink shadow-sm transition hover:border-slate-400 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2"
                @click="goBack"
              >
                Tagasi otsingusse
              </button>
            </div>
          </div>
        </div>
      </article>

      <section
        v-if="hasWarehouseData"
        class="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
      >
        <h2 class="text-lg font-semibold text-ink">Laoseisu detailid</h2>
        <div class="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <article
            v-for="(row, idx) in article.WarehouseQuantities"
            :key="idx"
            class="rounded-xl border border-slate-200 bg-slate-50 p-4"
          >
            <h3 class="text-sm font-semibold text-ink">
              {{ row.WarehouseName || row.Region || `Ladu ${idx + 1}` }}
            </h3>
            <dl class="mt-3 space-y-2 text-sm">
              <div
                v-for="entry in visibleWarehouseEntries(row)"
                :key="entry.key"
                class="flex items-center justify-between gap-4 text-slate-600"
              >
                <dt>{{ humanizeKey(entry.key) }}</dt>
                <dd class="font-semibold text-ink">
                  {{ formatValue(entry.value) }}
                </dd>
              </div>
            </dl>
          </article>
        </div>
      </section>
    </template>

    <!-- Not found -->
    <div
      v-else
      class="rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm"
    >
      <p class="text-slate-500">Andmeid ei leitud.</p>
      <button type="button" :class="[primaryButton, 'mt-6']" @click="goBack">
        Tagasi
      </button>
    </div>
  </div>
</template>

<script>
import { NET_PRICE_ADJUSTMENT } from "@/config/pricing";
import { isWheel } from "@/config/catalog";

const API_BASE = "https://saurusrehv-backend.azurewebsites.net/api";

export default {
  name: "RehviDetailView",
  data() {
    return {
      article: null,
      loading: false,
      error: null,
      imageLoaded: false,
      imageFailed: false,
      currentRequestId: null,
      primaryButton:
        "inline-flex items-center justify-center gap-2 rounded-lg bg-brand px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2",
    };
  },
  computed: {
    articleId() {
      const rawId = this.$route.params.articleId;
      const numericId = Number(rawId);
      return Number.isNaN(numericId) ? null : numericId;
    },
    storeArticle() {
      const id = this.articleId;
      if (id == null) return null;
      const map = this.$store.state?.articles?.articleMap;
      return map ? map[id] || null : null;
    },
    // Plain <img> URL (same as the results list), so the browser can reuse
    // the thumbnail it already downloaded instead of fetching it again.
    imageUrl() {
      const imageId = this.article?.ImageId;
      return imageId ? `${API_BASE}/ArticleImages/${imageId}` : null;
    },
    imageStatusText() {
      if (!this.imageUrl) return "Ilma pildita";
      if (this.imageFailed) return "Pildi laadimine ebaõnnestus.";
      return "Laen pilti…";
    },
    specTags() {
      const a = this.article;
      if (!a) return [];
      if (isWheel(a)) return this.wheelSpecTags(a);
      const tags = [];
      if (a.TyreClass) tags.push(a.TyreClass);
      if (a.SpeedIndex) tags.push(`Kiirusindeks ${a.SpeedIndex}`);
      if (a.LoadIndex) tags.push(`Koormusindeks ${a.LoadIndex}`);
      if (a.DotYear) tags.push(`DOT ${a.DotYear}`);
      return tags;
    },
    localStock() {
      return this.article?.QuantityAvailable ?? 0;
    },
    externalStock() {
      return this.article?.QuantityExternal ?? 0;
    },
    inquiryMailto() {
      const a = this.article;
      if (!a) return "mailto:saurusrehv@gmail.com";
      const subject = `Päring: ${a.ArticleText} (ID ${a.ArticleId})`;
      return `mailto:saurusrehv@gmail.com?subject=${encodeURIComponent(
        subject
      )}`;
    },
    hasWarehouseData() {
      if (!Array.isArray(this.article?.WarehouseQuantities)) {
        return false;
      }
      return this.article.WarehouseQuantities.some(
        (row) => this.visibleWarehouseEntries(row).length > 0
      );
    },
    netPriceValue() {
      if (!this.article || this.article.NetPrice == null) return null;
      const base = Number(this.article.NetPrice);
      if (Number.isNaN(base)) return null;
      return base + NET_PRICE_ADJUSTMENT;
    },
    vatPriceValue() {
      const net = this.netPriceValue;
      if (net == null) return null;
      return net * 1.24;
    },
    netPriceFormatted() {
      const net = this.netPriceValue;
      return net == null ? "—" : this.formatPrice(net);
    },
    vatPriceFormatted() {
      const gross = this.vatPriceValue;
      return gross == null ? "—" : this.formatPrice(gross);
    },
  },
  mounted() {
    this.handleRouteChange();
  },
  watch: {
    "$route.params.articleId"() {
      this.handleRouteChange();
    },
    storeArticle(newArticle) {
      if (newArticle && this.article !== newArticle) {
        this.applyArticle(newArticle);
      }
    },
    imageUrl() {
      this.imageLoaded = false;
      this.imageFailed = false;
    },
  },
  methods: {
    handleRouteChange() {
      this.currentRequestId = null;
      this.error = null;

      if (this.articleId == null) {
        this.loading = false;
        this.article = null;
        this.error = "Artikli ID puudub.";
        return;
      }

      const cachedArticle = this.storeArticle;
      if (cachedArticle) {
        this.applyArticle(cachedArticle);
        return;
      }

      this.fetchArticleFromApi(this.articleId);
    },
    applyArticle(article) {
      if (!article) return;
      this.article = article;
      this.loading = false;
      this.error = null;
    },
    async fetchArticleFromApi(articleId) {
      this.loading = true;
      this.error = null;
      this.article = null;

      const requestId = String(articleId);
      this.currentRequestId = requestId;

      try {
        const response = await fetch(`${API_BASE}/articles/${articleId}`);
        if (!response.ok) {
          throw new Error(`Viga andmete laadimisel (kood ${response.status})`);
        }
        const data = await response.json();
        if (this.currentRequestId !== requestId) {
          return;
        }
        if (data) {
          this.article = data;
          await this.$store.dispatch("articles/upsertArticle", data);
        } else {
          this.article = null;
        }
      } catch (err) {
        if (this.currentRequestId !== requestId) {
          return;
        }
        this.error = err?.message || "Tekkis ootamatu viga.";
        this.article = null;
      } finally {
        if (this.currentRequestId === requestId) {
          this.loading = false;
        }
      }
    },
    visibleWarehouseEntries(row) {
      if (!row || typeof row !== "object") {
        return [];
      }
      return Object.entries(row)
        .filter(([, value]) => value !== null && value !== undefined)
        .map(([key, value]) => ({ key, value }))
        .filter((entry) => !["WarehouseName", "Region"].includes(entry.key));
    },
    formatValue(value) {
      if (value === null || value === undefined || value === "") {
        return "—";
      }
      if (typeof value === "boolean") {
        return value ? "Jah" : "Ei";
      }
      if (typeof value === "number") {
        return this.decimalFormatter().format(value);
      }
      return value;
    },
    decimalFormatter() {
      return new Intl.NumberFormat("et-EE", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    currencyFormatter() {
      return new Intl.NumberFormat("et-EE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
      });
    },
    formatPrice(value, options = {}) {
      if (value === null || value === undefined || value === "") return "—";
      let numeric = Number(value);
      if (Number.isNaN(numeric)) return value;
      if (options.applyNetAdjustment) {
        numeric += NET_PRICE_ADJUSTMENT;
      }
      return this.currencyFormatter().format(numeric);
    },
    wheelSpecTags(a) {
      const format = (value) => this.decimalFormatter().format(value);
      const tags = [];
      if (a.Width && a.Diameter) {
        tags.push(`${format(a.Width)}J × ${format(a.Diameter)}"`);
      }
      if (a.NumberOfBolts && a.BoltCircle) {
        tags.push(`PCD ${a.NumberOfBolts}×${a.BoltCircle}`);
      }
      if (a.Offset != null) tags.push(`ET ${format(a.Offset)}`);
      if (a.CenterBore) tags.push(`CB ${format(a.CenterBore)} mm`);
      if (a.Color) tags.push(a.Color);
      if (a.MaximumLoad > 0) {
        tags.push(`Max koormus ${format(a.MaximumLoad)} kg`);
      }
      return tags;
    },
    humanizeKey(key) {
      if (!key) return "";
      const spaced = key
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .trim();
      return spaced.charAt(0).toUpperCase() + spaced.slice(1);
    },
    goBack() {
      // Return to the previous in-app page (keeps the search results and
      // scroll position); fall back to the catalogue when opened directly.
      if (window.history.state && window.history.state.back) {
        this.$router.back();
      } else {
        this.$router.push({
          name: this.$route.name === "VeljeDetail" ? "Veljed" : "Rehvid",
        });
      }
    },
  },
  beforeUnmount() {
    this.currentRequestId = null;
  },
};
</script>
